/**
 * 토스트 메시지 투명도 관리 시스템
 * 여러 토스트가 쌓일 때, 최신 토스트는 100%, 이전 것들은 20%씩 감소
 * 최신: 1.0 (100%) → 0.8 (80%) → 0.6 (60%) → 0.4 (40%) → 0.2 (20%)
 */

const OPACITY_LEVELS = [1.0, 0.8, 0.6, 0.4, 0.2];
const DEBOUNCE_DELAY = 50; // 50ms debounce

let opacityObserver: MutationObserver | null = null;
let updateTimeout: NodeJS.Timeout | null = null;

/**
 * 현재 표시 중인 토스트들의 투명도를 계산하고 적용
 */
export const updateToastOpacity = () => {
  const messageContainer = document.querySelector('.ant-message');
  if (!messageContainer) return;

  const toastWrappers = messageContainer.querySelectorAll(
    '.ant-message-notice-wrapper'
  ) as NodeListOf<HTMLElement>;

  if (toastWrappers.length === 0) return;

  // 역순으로 순회 (최신이 마지막 인덱스)
  toastWrappers.forEach((toast, index) => {
    const position = toastWrappers.length - 1 - index; // 0: 최신, 1: 이전, ...
    const opacityLevel = OPACITY_LEVELS[position] || 0.2;

    // 토스트의 투명도 설정
    toast.style.opacity = String(opacityLevel);

    // data attribute에 순서 정보 저장 (디버깅용)
    toast.setAttribute('data-toast-position', String(position));
    toast.setAttribute('data-toast-opacity', String(Math.round(opacityLevel * 100)));
  });
};

/**
 * Debounced 업데이트 (연속 호출 시 마지막 호출만 실행)
 */
const debouncedUpdateToastOpacity = () => {
  if (updateTimeout) {
    clearTimeout(updateTimeout);
  }

  updateTimeout = setTimeout(() => {
    updateToastOpacity();
    updateTimeout = null;
  }, DEBOUNCE_DELAY);
};

/**
 * 토스트 컨테이너 변경을 감시하고 투명도 업데이트
 */
export const initToastOpacityManager = () => {
  const messageContainer = document.querySelector('.ant-message');
  if (!messageContainer) {
    // 메시지 컨테이너가 아직 없으면 재시도
    setTimeout(initToastOpacityManager, 100);
    return;
  }

  // 기존 observer 정리
  if (opacityObserver) {
    opacityObserver.disconnect();
  }

  // 토스트 추가/제거 감시 (debounced 업데이트)
  opacityObserver = new MutationObserver(() => {
    debouncedUpdateToastOpacity();
  });

  opacityObserver.observe(messageContainer, {
    childList: true,
    subtree: true,
    characterData: false,
  });

  // 초기 업데이트
  updateToastOpacity();
};

/**
 * 토스트 opacity 관리 정지
 */
export const stopToastOpacityManager = () => {
  if (opacityObserver) {
    opacityObserver.disconnect();
    opacityObserver = null;
  }

  if (updateTimeout) {
    clearTimeout(updateTimeout);
    updateTimeout = null;
  }
};

/**
 * React Hook으로 사용하기 위한 래퍼
 */
export const useToastOpacityManager = () => {
  const React = require('react');

  React.useEffect(() => {
    initToastOpacityManager();

    return () => {
      stopToastOpacityManager();
    };
  }, []);
};